import { useEffect, useState } from "react";
import { Camera, ImagePlus, Loader2, AlertTriangle, Undo2, Upload, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Field, ConfirmDialog } from "./common";
import { AuthImage } from "./AuthImage";
import { api, errMsg } from "../lib/api";
import { fileToJpeg } from "../lib/image";
import { fmtDate, rp, todayISO } from "../lib/format";

export const BayarBadge = ({ bayar, testid, onClick }) => {
  const paid = bayar?.status === "sudah";
  const Icon = paid ? CheckCircle2 : XCircle;
  const cls = paid ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100" : "bg-red-50 text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-100";
  const Tag = onClick ? "button" : "span";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold transition-colors ${cls}`}>
      <Icon className="h-3.5 w-3.5" />{paid ? "Sudah Dibayar" : "Belum Dibayar"}
    </Tag>
  );
};

const CaraToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {[["tunai", "Tunai"], ["transfer", "Transfer"]].map(([v, l]) => (
      <button key={v} type="button" data-testid={`bayar-cara-${v}`} onClick={() => onChange(v)}
        className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${value === v ? "border-primary bg-primary text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>{l}</button>
    ))}
  </div>
);

export const FotoPicker = ({ value, onChange, prefix = "bayar-foto" }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { onChange(await fileToJpeg(file, 1280)); } catch (err) { toast.error(err.message); }
  };
  const Btn = ({ icon: Icon, label, capture, id }) => (
    <label data-testid={`${prefix}-${id}`} className="flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">
      <Icon className="h-4 w-4" />{label}
      <input data-testid={`${prefix}-${id}-input`} type="file" accept="image/*" capture={capture} className="hidden" onChange={pick} />
    </label>
  );
  return (
    <div className="space-y-2">
      <div className="flex gap-2"><Btn icon={Camera} label="Kamera" capture="environment" id="kamera" /><Btn icon={ImagePlus} label="Galeri" id="galeri" /></div>
      {value && <img data-testid={`${prefix}-preview`} src={value} alt="Bukti" className="max-h-48 rounded-lg border border-gray-200 object-contain" />}
    </div>
  );
};

export const BayarDialog = ({ row, period, open, onOpenChange, onDone }) => {
  const [f, setF] = useState({ tanggal: todayISO(), cara: "", foto: null, catatan: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ tanggal: todayISO(), cara: "", foto: null, catatan: "" }); }, [open]);
  if (!row) return null;
  const nol = row.gaji_bersih === 0;

  const submit = async () => {
    if (!f.tanggal) return toast.error("Tanggal bayar wajib diisi");
    if (!nol && !f.cara) return toast.error("Pilih cara bayar: Tunai atau Transfer");
    if (!nol && f.cara === "transfer" && !f.foto) return toast.error("Foto bukti transfer wajib diunggah");
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/${period}/bayar/${row.anak_id}`, { tanggal_bayar: f.tanggal, cara: nol ? null : f.cara, foto: f.foto, catatan: f.catatan || null });
      (data.peringatan || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      toast.success(data.lunas ? `${row.anak_nama} dibayar — semua anak sudah dibayar, periode LUNAS` : `${row.anak_nama} ditandai sudah dibayar`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bayar-dialog" className="max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Tandai dibayar: {row.anak_nama}</DialogTitle>
          <DialogDescription>Data voucher & kasbon anak ini di periode ini akan dikunci.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-emerald-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">Jumlah dibayar (gaji bersih)</p>
          <p data-testid="bayar-jumlah" className="font-mono text-2xl font-bold text-primary">{rp(row.gaji_bersih)}</p>
          {nol && <p data-testid="bayar-nol-note" className="text-sm text-emerald-900">Rp0 — tidak ada pembayaran (gaji habis untuk potongan kasbon).</p>}
        </div>
        {row.peringatan_sebelum && (
          <p data-testid="bayar-peringatan-sebelum" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{row.peringatan_sebelum} Tetap bisa dibayar sekarang.
          </p>
        )}
        <div className="space-y-4">
          <Field label="Tanggal bayar"><Input type="date" data-testid="bayar-tanggal" className="h-10" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} /></Field>
          {!nol && <Field label="Cara bayar"><CaraToggle value={f.cara} onChange={(v) => setF({ ...f, cara: v })} /></Field>}
          {!nol && f.cara && (
            <Field label={f.cara === "transfer" ? "Foto bukti transfer (wajib)" : "Foto bukti (opsional)"}>
              <FotoPicker value={f.foto} onChange={(v) => setF({ ...f, foto: v })} />
            </Field>
          )}
          <Field label="Catatan (opsional)"><Input data-testid="bayar-catatan" className="h-10" value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" data-testid="bayar-cancel" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="bayar-submit" disabled={busy} className="h-10 bg-emerald-600 hover:bg-emerald-500" onClick={submit}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Tandai Sudah Dibayar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, children, testid }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p><p data-testid={testid} className="font-medium text-gray-900">{children}</p></div>
);

export const BayarInfo = ({ bayar, buktiSrc, prefix = "bayar-detail" }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3 text-sm">
      <Info label="Tanggal bayar" testid={`${prefix}-tanggal`}>{fmtDate(bayar.tanggal_bayar)}</Info>
      <Info label="Cara bayar" testid={`${prefix}-cara`}>{bayar.cara_label || "-"}</Info>
      {bayar.jumlah != null && <Info label="Jumlah" testid={`${prefix}-jumlah`}>{rp(bayar.jumlah)}</Info>}
      {bayar.catatan && <Info label="Catatan" testid={`${prefix}-catatan`}>{bayar.catatan}</Info>}
    </div>
    {bayar.bukti_belum && (
      <p data-testid={`${prefix}-bukti-belum`} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900"><AlertTriangle className="h-4 w-4" />Bukti belum diunggah</p>
    )}
    {bayar.has_bukti && buktiSrc && (
      <a href="#bukti" onClick={(e) => e.preventDefault()} className="block">
        <AuthImage src={buktiSrc} testid={`${prefix}-bukti`} className="max-h-80 w-full rounded-lg border border-gray-200 !object-contain" alt="Bukti transfer" />
      </a>
    )}
  </div>
);

export const DetailBayarDialog = ({ row, period, canRevert, revertNote, open, onOpenChange, onDone }) => {
  const [foto, setFoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [ver, setVer] = useState(0);
  useEffect(() => { if (open) setFoto(null); }, [open]);
  if (!row) return null;
  const base = `/payroll/${period}/bayar/${row.anak_id}`;

  const upload = async () => {
    setBusy(true);
    try { await api.put(`${base}/bukti`, { foto }); toast.success("Bukti pembayaran diunggah"); setFoto(null); setVer((v) => v + 1); onOpenChange(false); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revert = async () => {
    setConfirm(false);
    try {
      const { data } = await api.delete(base);
      (data.peringatan || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      toast.success(`${row.anak_nama} dikembalikan ke Belum Dibayar`); onOpenChange(false); onDone();
    }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="bayar-detail-dialog" className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">{row.anak_nama}<BayarBadge bayar={row.bayar} /></DialogTitle>
            <DialogDescription>Detail pembayaran gaji periode ini.</DialogDescription>
          </DialogHeader>
          <BayarInfo bayar={row.bayar} buktiSrc={`${base}/bukti?v=${ver}`} />
          {row.bayar.cara && row.bayar.cara !== "nihil" && (
            <div className="space-y-2 rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{row.bayar.has_bukti ? "Ganti foto bukti" : "Unggah foto bukti"}</p>
              <FotoPicker value={foto} onChange={setFoto} prefix="bayar-detail-foto" />
              {foto && <Button data-testid="bayar-detail-upload" disabled={busy} className="h-9 w-full rounded-full" onClick={upload}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Simpan bukti</Button>}
            </div>
          )}
          <DialogFooter className="gap-2">
            {revertNote && <p className="text-xs text-gray-500 sm:mr-auto">{revertNote}</p>}
            <Button data-testid="bayar-detail-batal" variant="outline" disabled={!canRevert} className="h-10 border-red-200 text-red-700 hover:bg-red-50" onClick={() => setConfirm(true)}>
              <Undo2 className="mr-1.5 h-4 w-4" />Jadikan Belum Dibayar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} destructive testid="bayar-batal-confirm" title={`Kembalikan ${row.anak_nama} ke Belum Dibayar?`}
        description={`Catatan pembayaran & foto bukti akan dihapus dan data anak ini bisa diubah lagi.${row.lunas ? " Periode ini sudah lunas — statusnya akan kembali menjadi belum lunas." : ""}${row.dibayar_setelahnya ? ` Perhatian: anak ini sudah dibayar di periode ${row.dibayar_setelahnya} — angka kasbon periode tersebut perlu dicek ulang.` : ""}`}
        confirmLabel="Ya, jadikan belum dibayar" onConfirm={revert} />
    </>
  );
};

export const BayarSemuaDialog = ({ open, onOpenChange, sisa, sisaRp, onConfirm }) => {
  const [f, setF] = useState({ tanggal: todayISO(), cara: "tunai" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ tanggal: todayISO(), cara: "tunai" }); }, [open]);
  const go = async () => { setBusy(true); await onConfirm({ tanggal_bayar: f.tanggal, cara: f.cara }); setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bayar-semua-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Tandai semua sudah dibayar?</DialogTitle>
          <DialogDescription>{sisa} anak yang belum dibayar ({rp(sisaRp)}) akan ditandai dibayar dan periode menjadi LUNAS & terkunci.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Tanggal bayar"><Input type="date" data-testid="bayar-semua-tanggal" className="h-10" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} /></Field>
          <Field label="Cara bayar (untuk semua)"><CaraToggle value={f.cara} onChange={(v) => setF({ ...f, cara: v })} /></Field>
          {f.cara === "transfer" && <p data-testid="bayar-semua-bukti-note" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />Bukti transfer bisa diunggah per anak nanti (status "bukti belum diunggah").</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" data-testid="bayar-semua-cancel" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="bayar-semua-confirm" disabled={busy || !f.tanggal} className="h-10 bg-emerald-600 hover:bg-emerald-500" onClick={go}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ya, tandai semua</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
